import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Collapse } from '@mui/material';
import { Close as CloseIcon, CheckCircle, Warning, Info, Error } from '@mui/icons-material';
import Confirmpopup from './Confirmpopup';
import { useNavigate } from 'react-router-dom';
import { getUserFromLocalStorage } from '../../../utils/utils';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import KycOnboardingPopup from '../Popups/KycOnboardingPopup';

export const KYC_STATUS = {
  NOT_STARTED: 'not_started',
  DOCUMENTS_PENDING: 'documents_pending',
  PENDING: 'pending',
  ACTION_REQUIRED: 'action_required',
  ON_HOLD: 'on_hold',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  AWAITING_BANK_LINK: 'awaiting_bank_link',
  BANK_LINK_PENDING: 'bank_link_pending',
  AWAITING_DEPOSIT: 'awaiting_deposit',
  COMPLETED: 'completed',
} as const;

export type KycStatusValue = typeof KYC_STATUS[keyof typeof KYC_STATUS];

interface OnboardingStatusBarProps {
  onDismiss?: () => void;
}

interface OnboardingStatus {
  type: 'success' | 'warning' | 'info' | 'error';
  title: string;
  message: string;
  actionText?: string;
  actionUrl?: string;
  actionState?: any;
  statusKey: string;
  priority: number;
}

const DISMISSED_KEY = 'onboardingStatusBar_dismissed';

const getDismissedStatuses = (): string[] => {
  try {
    const v = localStorage.getItem(DISMISSED_KEY);
    return v ? JSON.parse(v) : [];
  } catch {
    return [];
  }
};

const addDismissedStatus = (key: string) => {
  try {
    const dismissed = getDismissedStatuses();
    if (!dismissed.includes(key)) {
      dismissed.push(key);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }
  } catch (e) {
    console.error('Error saving dismissed status:', e);
  }
};

const isStatusDismissed = (key: string) => getDismissedStatuses().includes(key);

const STATUS_COLOR: Record<string, string> = {
  success: '#4edf6d',
  warning: '#ffa726',
  info: '#4A7AF2',
  error: '#f44336',
};

const STATUS_GRADIENT: Record<string, string> = {
  success: 'linear-gradient(135deg, #1e3a2e 0%, #2d5a3d 50%, #3d7a4d 100%)',
  warning: 'linear-gradient(135deg, #3d2e1e 0%, #5a3d2d 50%, #7a4d3d 100%)',
  info: 'linear-gradient(135deg, #1e2e3d 0%, #2d3d5a 50%, #3d4d7a 100%)',
  error: 'linear-gradient(135deg, #3d1e1e 0%, #5a2d2d 50%, #7a3d3d 100%)',
};

const StatusIcon = ({ type }: { type: string }) => {
  const sx = { fontSize: '18px', color: '#ffffff' };
  switch (type) {
    case 'success': return <CheckCircle sx={sx} />;
    case 'warning': return <Warning sx={sx} />;
    case 'error': return <Error sx={sx} />;
    default: return <Info sx={sx} />;
  }
};

function buildStatusEntry(
  kycStatus: KycStatusValue,
  backendMessage: string,
  canCreateAccount: boolean,
  isLinkBankAvailable: boolean,
  isAus: boolean
): OnboardingStatus | null {
  switch (kycStatus) {
    case KYC_STATUS.NOT_STARTED:
      return {
        type: 'info',
        title: 'Create Your Trading Account',
        message: backendMessage || 'Start your trading journey with us.',
        actionText: 'Create Account',
        actionUrl: '/createApplication',
        priority: 10,
        statusKey: 'kyc_not_started',
      };

    case KYC_STATUS.DOCUMENTS_PENDING:
      return {
        type: 'info',
        title: 'Complete Your Application',
        message: backendMessage || 'Please complete and submit your identification documents to proceed.',
        actionText: "Continue Application",
        actionUrl: canCreateAccount ? '/createApplication' : undefined,
        priority: 10,
        statusKey: 'kyc_documents_pending',
      };

    case KYC_STATUS.ACTION_REQUIRED:
      return {
        type: 'warning',
        title: 'Action Required — Update Your Application',
        message: backendMessage || 'Resubmission requested. Please review and update your details.',
        actionText: canCreateAccount ? 'Continue Application' : undefined,
        actionUrl: canCreateAccount ? '/createApplication' : undefined,
        priority: 9,
        statusKey: 'kyc_action_required',
      };

    case KYC_STATUS.PENDING:
      return {
        type: 'info',
        title: 'Application Under Review',
        message: backendMessage || 'Your application is being reviewed. This typically takes 1–2 business days.',
        priority: 5,
        statusKey: 'kyc_pending',
      };

    case KYC_STATUS.ON_HOLD:
      return {
        type: 'warning',
        title: 'Application On Hold',
        message: backendMessage || 'Your application is on hold and being reviewed. No action required.',
        priority: 6,
        statusKey: 'kyc_on_hold',
      };

    case KYC_STATUS.REJECTED:
      return {
        type: 'error',
        title: 'Application Rejected',
        message: backendMessage || 'Your application was rejected. Please contact support.',
        priority: 8,
        statusKey: 'kyc_rejected',
      };

    case KYC_STATUS.AWAITING_BANK_LINK:
      return {
        type: 'warning',
        title: 'Link Your Bank Account',
        message: backendMessage || 'KYC approved! Link your bank account to continue.',
        actionText: 'Link Bank Account',
        actionUrl: '/linkbankaccount',
        priority: 7,
        statusKey: 'kyc_awaiting_bank_link',
      };

    case KYC_STATUS.BANK_LINK_PENDING:
      return {
        type: 'info',
        title: 'Bank Account Linking in Progress',
        message: backendMessage || 'We\'re linking your bank account. This may take a moment.',
        actionText: 'Check Status',
        actionUrl: '/linkbankaccount',
        priority: 4,
        statusKey: 'kyc_bank_link_pending',
      };

    case KYC_STATUS.AWAITING_DEPOSIT:
      return {
        type: 'warning',
        title: 'Deposit Funds to Start Trading',
        message: backendMessage || 'Bank linked! Deposit funds to start your crypto trading journey.',
        actionText: 'Deposit Funds',
        actionUrl: '/transfer',
        actionState: { canLinkBank: isLinkBankAvailable, isAus },
        priority: 7,
        statusKey: 'kyc_awaiting_deposit',
      };

    case KYC_STATUS.COMPLETED:
      // Fully onboarded — show booster loan nudge if not already opted in (handled separately below)
      return null;

    default:
      return null;
  }
}

const OnboardingStatusBar: React.FC<OnboardingStatusBarProps> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const user = getUserFromLocalStorage();

  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState<OnboardingStatus | null>(null);
  const [showEConsentPopup, setShowEConsentPopup] = useState(false);
  const [showKycOnboardingPopup, setShowKycOnboardingPopup] = useState(false);

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      setIsLoading(true);
      const response = await Axios.get(UrlConstant.Sumsub.CheckAccountStatus);

      if (!response.data?.success) return;

      const { status, message, canCreateAccount, isLinkBankAvailable, isAus } = response.data.data as {
        status: KycStatusValue;
        message: string;
        canCreateAccount: boolean;
        isLinkBankAvailable: boolean;
        isAus: boolean;
      };

      // ── e-consent gate: highest priority regardless of KYC status ────────
      if (!user?.econsentAccepted) {
        const statusKey = 'econsent_required';
        if (!isStatusDismissed(statusKey)) {
          setCurrentStatus({
            type: 'warning',
            title: 'E-Consent Required',
            message: 'You need to accept e-consent before proceeding with account creation.',
            actionText: 'Accept E-Consent',
            actionUrl: '/e-consent',
            priority: 15,
            statusKey,
          });
          return;
        }
      }

      // ── KYC-driven status ─────────────────────────────────────────────────
      const kycEntry = buildStatusEntry(status, message, canCreateAccount, isLinkBankAvailable, isAus);

      if (kycEntry && !isStatusDismissed(kycEntry.statusKey)) {
        setCurrentStatus(kycEntry);
        return;
      }

      // ── Fully onboarded — show booster loan nudge if not yet opted in ─────
      if (status === KYC_STATUS.COMPLETED) {
        const isBoosterActive = user?.boosterLoanOptIn === true || !!user?.boosterloan_id;
        if (!isBoosterActive && !isStatusDismissed('booster_loan_registration')) {
          setCurrentStatus({
            type: 'info',
            title: 'Activate Booster Loan',
            message: 'Register for booster loan to unlock instant trading power. Invest Now, Pay Later!',
            actionText: 'Register Now',
            actionUrl: '/boosterlending',
            priority: 1,
            statusKey: 'booster_loan_registration',
          });
        }
      }
    } catch (error) {
      console.error('OnboardingStatusBar — KYC status fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = () => {
    // If action requires e-consent first, intercept navigation
    if (
      currentStatus?.statusKey === 'kyc_not_started' ||
      currentStatus?.statusKey === 'kyc_action_required'
    ) {
      if (!user?.econsentAccepted) {
        setShowEConsentPopup(true);
      } else {
        setShowKycOnboardingPopup(true);
      }
      return;
    }

    if (currentStatus?.actionUrl) {
      if (currentStatus.actionState) {
        navigate(currentStatus.actionUrl, { state: currentStatus.actionState });
      } else {
        navigate(currentStatus.actionUrl);
      }
    }
  };

  const handleDismiss = () => {
    if (currentStatus?.statusKey) {
      addDismissedStatus(currentStatus.statusKey);
    }
    setIsVisible(false);
    onDismiss?.();
  };

  if (isLoading || !currentStatus || !isVisible) return null;

  const color = STATUS_COLOR[currentStatus.type] ?? STATUS_COLOR.info;
  const gradient = STATUS_GRADIENT[currentStatus.type] ?? STATUS_GRADIENT.info;

  return (
    <Collapse in={isVisible}>
      <Box
        sx={{
          width: '100vw',
          height: '50px',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          background: gradient,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 30px',
          borderBottom: `3px solid ${color}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Dark overlay for readability */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
          }}
        />

        {/* Left — icon + text */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <StatusIcon type={currentStatus.type} />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2, color: '#ffffff' }}>
              {currentStatus.title}
            </Typography>
            <Typography sx={{ fontSize: '13px', lineHeight: 1.3, color: '#d1d5db' }}>
              {currentStatus.message}
            </Typography>
          </Box>
        </Box>

        {/* Right — action + dismiss */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 2 }}>
          {currentStatus.actionText && (
            <Button
              className="btn btn-blue"
              onClick={handleAction}
              sx={{
                padding: '4px 12px',
                fontSize: '11px',
                fontWeight: 600,
                borderRadius: '50px',
                textTransform: 'none',
                minWidth: '80px',
                height: '28px',
              }}
            >
              {currentStatus.actionText}
            </Button>
          )}

          <IconButton
            onClick={handleDismiss}
            sx={{
              color: '#9ca3af',
              width: '20px',
              height: '20px',
              '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* E-Consent gate popup */}
      {showEConsentPopup && (
        <Confirmpopup
          open={showEConsentPopup}
          onClose={() => setShowEConsentPopup(false)}
          onConfirm={() => {
            setShowEConsentPopup(false);
            navigate('/e-consent');
          }}
          message="You need to accept e-consent before proceeding with account creation."
        />
      )}
      {showKycOnboardingPopup && (
        <KycOnboardingPopup
          open={showKycOnboardingPopup}
          onClose={() => setShowKycOnboardingPopup(false)}
          source="Dashboard_Header_OnboardingStatusBar"
        />
      )}
    </Collapse>
  );
};

// Export the clear function for use in logout
export const clearOnboardingStatusBarDismissals = () => {
  try {
    localStorage.removeItem(DISMISSED_KEY);
  } catch (e) {
    console.error('Error clearing dismissed statuses:', e);
  }
};

export default OnboardingStatusBar;